"use client";

import { Calendar, House, Route, Star, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { useTranslator } from "@/lib/translation-context";

import { useActiveSection } from "./_tour-nav.client";
import type { TourTab } from "./_section-order";
import styles from "./_tour-bottom-tabs.module.css";

/**
 * The mobile guide's `.tabbar` — four destinations pinned to the bottom of the
 * page, under the price bar.
 *
 * It is NOT a second copy of the section tabs. The strip at the top indexes
 * every section the operator configured, in their order, and is how a reader
 * moves around a section they are already in. This is the app-shell shortcut: a
 * fixed four, always in the same place, for the four things a traveller comes
 * back to on a 12,000px page. It is a SUBSET of the top strip, curated by which
 * slugs have an icon — the prototype's own mobile bar is the same four — so a
 * tour with Map, FAQs or custom tabs still shows exactly four here while the
 * strip above shows all of them.
 *
 * Entries whose section is not on the page are dropped rather than rendered
 * dead: an operator who switched Reviews off gets three tabs, not four with one
 * that scrolls nowhere.
 */
const BOTTOM_TAB_ICONS: Record<string, LucideIcon> = {
  overview: House,
  dates: Calendar,
  // `Route` — the same icon the itinerary section uses for its own Distance
  // fact, and the one thing on this bar that has to say "day by day". It was
  // `SlidersHorizontal`, which is the universal mark for FILTERS: on a page
  // that has a real filter bar thirty pixels above it, a second sliders icon
  // labelled Itinerary invites the tap that changes the month.
  itinerary: Route,
  reviews: Star,
};

/** Below this there is nothing to switch BETWEEN, and a bar of one costs 56px
 *  of a 844px screen to say where you already are. */
const MIN_TABS = 2;

/** Backstop for browsers without `scrollend` (Safari before 26) — long enough
 *  to cover a full-page smooth scroll. Mirrors the section tab bar's own. */
const SCROLL_SETTLE_FALLBACK_MS = 1200;

/**
 * How much room this bar takes at the bottom of the viewport, published for the
 * price bar to stack on.
 *
 * TWO PROPERTIES, and the split is what makes the price bar correct on a page
 * where this bar does not render (fewer than two sections, or the operator hid
 * them):
 *
 *  - `--tc-tour-bars-inset` — how far UP the price bar has to start. This bar's
 *    height plus the home indicator, because this bar already pads for that
 *    itself. Unset, the price bar falls back to `0` and sits flush.
 *  - `--tc-tour-bookbar-pad` — set to `0px` here, because with this bar beneath
 *    it the price bar must NOT pad for the home indicator a second time. Unset,
 *    the price bar pads itself, which is exactly right when it is the only bar.
 *
 * It used to be one property, and the fallback was the safe-area inset — so on
 * a page without this bar the price bar started 34px above the bottom of an
 * iPhone with nothing behind the gap, and `<body>` reserved no room at all, so
 * the bar covered the last 64px of the operator's footer. Ownership of the
 * `<body>` reservation moved with it: the price bar always renders, so the
 * price bar declares it.
 *
 * A PLAIN `<style>` — no `href`, no `precedence` — and both halves of that
 * matter, because the two obvious alternatives are each wrong in their own way.
 *
 * With `href`+`precedence`, React treats it as a hoistable resource: on unmount
 * it decrements an internal count and LEAVES THE ELEMENT IN `<head>`. These
 * values describe a bar only this route has, so after a client-side navigation
 * they would keep describing bars that are no longer on screen — and collide
 * with `mobile-quicknav`'s own `body{padding-bottom}` at the same precedence,
 * where the winner is decided by which page you happened to open second.
 *
 * Setting the properties from an EFFECT instead fixes the leak and costs
 * layout shift, which is worse. The price bar's `bottom` reads these values, so
 * server-side it painted flush at 0 and jumped up 56px once the effect ran:
 * measured CLS 0.000 → 0.014 on mobile, the single largest shift on the page.
 *
 * A plain `<style>` is neither. React renders it as an ordinary element — so it
 * is in the SSR HTML before the bar paints — and removes it on unmount like any
 * other node. Server-correct and cleaned up.
 */
const TAB_BAR_HEIGHT = "56px";
const TOUR_BARS_INSET_CSS =
  `@media (width < 64rem){` +
  `:root{` +
  // How far UP the price bar starts: this bar's height plus the home
  // indicator, because this bar already pads for that itself.
  `--tc-tour-bars-inset:calc(${TAB_BAR_HEIGHT} + env(safe-area-inset-bottom,0px));` +
  // With this bar underneath it, the price bar must NOT pad for the home
  // indicator a second time.
  `--tc-tour-bookbar-pad:0px` +
  `}` +
  `}`;

interface TourBottomTabsProps {
  /** Every section id actually rendered on this page, including `dates`, IN
   *  THE ORDER THE PAGE RENDERS THEM — which is the operator's configured tab
   *  order. The bar is built by filtering this, so a caller that appends a
   *  slug rather than placing it puts that tab at the end of the bar while its
   *  section sits in the middle of the page. */
  sectionSlugs: readonly string[];
  /** The operator's configured tabs, for their labels. A tenant who renamed
   *  "Itinerary" to "Day by day" gets their own word here too; a slug with no
   *  matching tab falls back to ours. */
  tabs: readonly TourTab[];
}

export function TourBottomTabs({ sectionSlugs, tabs }: TourBottomTabsProps) {
  const t = useTranslator();
  const fallbackLabels: Record<string, string> = {
    overview: t("Overview"),
    dates: t("Dates"),
    itinerary: t("Itinerary"),
    reviews: t("Reviews"),
  };

  // IN PAGE ORDER, which is the operator's configured tab order — `sectionSlugs`
  // arrives already sorted by it. A fixed order was the first shape of this,
  // argued from muscle memory; it is wrong on a page whose sections an operator
  // can re-arrange. An operator who puts Reviews above Itinerary got a bar
  // reading Itinerary, Reviews over a page reading Reviews, Itinerary — so the
  // bar ran backwards against the scroll, and the highlight appeared to jump
  // left as the reader moved down. Only slugs with an icon are offered; the
  // other sections are reachable by scrolling and by the top strip.
  const entries = sectionSlugs
    .filter((slug) => slug in BOTTOM_TAB_ICONS)
    .map((slug) => ({
      slug,
      Icon: BOTTOM_TAB_ICONS[slug]!,
      label:
        tabs.find((tab) => tab.slug === slug)?.label ?? fallbackLabels[slug]!,
    }));

  // MEMOISED on the slug list, not rebuilt per render. `useActiveSection`'s
  // observer effect depends on the array it is handed, so a fresh one every
  // render tears down and rebuilds an IntersectionObserver every render —
  // which on a page holding this INP budget is not a small waste. Labels are
  // not in the key because the spy reads slugs only.
  const slugKey = entries.map((e) => e.slug).join(",");
  const spyTabs = useMemo<TourTab[]>(
    () =>
      slugKey
        ? slugKey.split(",").map((slug) => ({ slug, label: slug }) as TourTab)
        : [],
    [slugKey],
  );
  // `navRef` IS attached (see the `<nav ref={navRef}>` below) and must stay
  // attached. `useActiveSection` needs a node to resolve `markerOffset` from,
  // and for THIS bar that resolution deliberately walks past the bar itself:
  // `nav.closest("[data-tour-tab-bar]")` misses, because a bottom-fixed bar is
  // not inside the top strip, so `_tour-nav.client.ts` falls through to the
  // document-wide lookup and measures the TOP strip instead. That is correct —
  // the top strip is what in-page anchors land under, so it is the offset every
  // section boundary must be judged against.
  //
  // This comment said the opposite until 2026-09-17 ("`navRef` stays empty …
  // falls back to the same constant"), which describes the BUG
  // `_tour-nav.client.ts` records fixing: a 52px fallback marker against the
  // real 113px meant tapping "Dates" scrolled correctly and then lit
  // "Overview". Do not "simplify" by dropping the ref or the document-wide
  // lookup.
  const navRef = useRef<HTMLElement>(null);
  const { activeSlug, selectOnClick, endClickScroll } = useActiveSection(
    spyTabs,
    navRef,
  );

  // The tap itself is handled by the SECTION TAB BAR's document-level click
  // listener, which already intercepts every in-page anchor on this page and
  // lands it on the same marker (see `inPageLinkTarget` — "deliberately NOT
  // limited to tab slugs"). So these are ordinary `<a href="#slug">`: no second
  // scroll implementation to keep in step with the first, and they still work
  // before hydration.
  //
  // What this bar DOES own is its own highlight during that scroll. Without the
  // lock its observer fires at every section the animation passes and the
  // selected tab flickers down the page ahead of the reader.
  const releaseLock = useRef<(() => void) | null>(null);
  useEffect(() => () => releaseLock.current?.(), []);

  const lockUntilSettled = (slug: string): void => {
    selectOnClick(slug);
    releaseLock.current?.();
    const done = () => {
      endClickScroll();
      releaseLock.current = null;
    };
    // Read into a boolean first: `if ("onscrollend" in window)` narrows
    // `window` itself, and the else branch then types as `never`. Same trap
    // `whenScrollSettles` documents in the section tab bar.
    const hasScrollEnd = "onscrollend" in window;
    if (hasScrollEnd) {
      window.addEventListener("scrollend", done, { once: true });
      releaseLock.current = () => window.removeEventListener("scrollend", done);
    } else {
      const timer = window.setTimeout(done, SCROLL_SETTLE_FALLBACK_MS);
      releaseLock.current = () => window.clearTimeout(timer);
    }
  };

  if (entries.length < MIN_TABS) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: TOUR_BARS_INSET_CSS }} />
      <nav
        ref={navRef}
        // NOT "Tour Sections" — that is the top strip's name, and two landmarks
        // sharing one accessible name is an axe `landmark-unique` failure and,
        // more to the point, a screen-reader user hearing the same name twice
        // in the landmark list has no way to tell which is which. The two are
        // genuinely different things: the strip indexes every section the
        // operator configured, this is the fixed four-shortcut app bar.
        aria-label={t("Tour Shortcuts")}
        // Hidden from the breakpoint where the desktop rail appears — the same
        // number the page grid and the price bar use. Two of these three
        // disagreeing is what once showed a sticky rail and a fixed bar at the
        // same time.
        className={styles.bar}
      >
        {entries.map(({ slug, Icon, label }) => {
          const active = slug === activeSlug;
          return (
            <a
              key={slug}
              href={`#${slug}`}
              aria-current={active ? "true" : undefined}
              onClick={() => lockUntilSettled(slug)}
              className={styles.tab}
            >
              <Icon
                aria-hidden
                className={styles.icon}
                // The guide thickens the stroke on the selected tab rather than
                // swapping to a filled icon — the colour already says which one
                // it is, and a fill would make one icon read as a different
                // family from the other three.
                strokeWidth={active ? 2.3 : 1.9}
              />
              <span className={styles.label}>{label}</span>
            </a>
          );
        })}
      </nav>
    </>
  );
}
